import {
  EquivalentSecurityListId,
  SecurityId,
} from "@fartherfinance/frontend/api/Types";

import { EquivalentSecurityListWithInfo } from "@src/multiCustodian/components/Admin/EquivalentSecurities/useEquivalentSecurityListMembersWithInfo";

interface ESMatch {
  listIndex: number;
  listName: string;
  matches: SecurityId[];
}

type ESListLookup = Record<EquivalentSecurityListId, ESMatch>;

type SecToESListLookup = Record<SecurityId, EquivalentSecurityListId>;

export interface ESAndListLookup {
  securities: SecToESListLookup;
  lists: ESListLookup;
}

export const emptyLookup = {
  securities: {} as SecToESListLookup,
  lists: {} as ESListLookup,
} as ESAndListLookup;

interface ItemWithSecurityId {
  securityId: SecurityId;
}

const createEquivalentSecurityAndEsListLookup = (
  itemsWithSecurityId: ItemWithSecurityId[],
  equivalentSecurityList: EquivalentSecurityListWithInfo[]
) => {
  const lookup = itemsWithSecurityId.reduce((acc, cur) => {
    const matchIndex = equivalentSecurityList.findIndex((list) =>
      list.members.some((sec) => sec.securityId === cur.securityId)
    );
    const match = equivalentSecurityList[matchIndex];

    if (matchIndex === -1 || match === undefined) {
      return acc;
    }

    const curMatches = acc.lists[match.listId]?.matches ?? [];

    return {
      securities: {
        ...acc.securities,
        [cur.securityId]: match.listId,
      },
      lists: {
        ...acc.lists,
        [match.listId]: {
          listIndex: matchIndex + 1,
          listName: match.name,
          matches: curMatches.includes(cur.securityId)
            ? [...curMatches]
            : [...curMatches, cur.securityId],
        },
      },
    };
  }, emptyLookup);

  const securityIds = Object.keys(lookup.securities) as SecurityId[];

  // Filter out ES securities that don't have another match in the same list
  return securityIds.reduce((acc, cur) => {
    const esListId = lookup.securities[cur];
    const esListData = lookup.lists[esListId];
    const hasMoreThanOneMatch = esListData?.matches.length >= 2;

    if (hasMoreThanOneMatch) {
      // include
      return {
        securities: {
          ...acc.securities,
          [cur]: esListId,
        },
        lists: {
          ...acc.lists,
          [esListId]: {
            listIndex: esListData.listIndex,
            listName: esListData.listName,
            matches: [...esListData.matches],
          },
        },
      };
    }

    // don't include
    return {
      securities: {
        ...acc.securities,
      },
      lists: {
        ...acc.lists,
      },
    };
  }, emptyLookup);
};

export default createEquivalentSecurityAndEsListLookup;
